import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MCText, UploadProgress, View } from 'components/basic';
import { useFileType, useUploadProgress, useDownload, useCountdown } from './hooks';
import { boxStyle, boxShadowWebExtra, rowBoxStyle } from './styles';
import { onFirstView, openSlideMedia } from './utils';
import CloseButton from './CloseButton';
import CountDownBadge from './CountDownBadge';
import FileDetail from './FileDetail';
import ImagePreview from './ImagePreview';
import VideoPreview from './VideoPreview';
import AudioPreview from './AudioPreview';
import DcmPreview from './DcmPreview';
import GenericFilePreview from './GenericFilePreview';
import { lang, remCss, events } from 'src/helpers';
import LANG_CONST from 'src/helpers/lang/const';

// Outer wrapper — uses the shared View component (not a raw <div>) so the
// layout can later be copied to React Native with no changes.
// Template 1: fixed-size card with box-shadow (the thumbnail + overlay design).
// Template 2: horizontal row that grows to fit its content (thumbnail + detail).
function FilePreviewBoxContainer({ template = 1, width = 150, height = 166, className, children }) {
  if (template === 2) {
    return (
      <View data-testid='file-box' className={className} style={rowBoxStyle}>
        {children}
      </View>
    );
  }
  return (
    <View
      data-testid='file-box'
      className={className}
      style={{ ...boxStyle, ...boxShadowWebExtra, width, height }}
    >
      {children}
    </View>
  );
}

// Renders the preview area for the current file type.
function renderPreviewByType({
  type,
  file,
  propFile,
  width,
  height,
  mediaRerender,
  onImgClick,
  onDcmClick,
  thumbnail,
  downloading,
  onDownloadClick,
  onAudioClick,
  fileId,
  onlyPreview,
  expired,
  styles,
  videoProps
}) {
  switch (type) {
    case 'image':
      return (
        <ImagePreview
          file={file}
          width={width}
          height={height}
          onClick={onImgClick}
          styles={styles}
        />
      );
    case 'video':
      return (
        <VideoPreview
          file={propFile}
          width={width}
          height={height}
          mediaRerender={mediaRerender}
          onClick={onImgClick}
          styles={styles}
          videoProps={videoProps}
        />
      );
    case 'audio':
      return (
        <AudioPreview
          file={file}
          fileId={fileId}
          onlyPreview={onlyPreview}
          styles={styles}
          onAudioClick={onAudioClick}
        />
      );
    case 'dcm':
      return <DcmPreview width={width} onClick={onDcmClick} styles={styles} />;
    default:
      return (
        <GenericFilePreview
          file={file}
          type={type}
          width={width}
          thumbnail={thumbnail}
          downloading={downloading}
          onClick={onDownloadClick}
          styles={styles}
          expired={expired}
        />
      );
  }
}

function FilePreviewBox(props) {
  const {
    file: propFile,
    fileId,
    styles = { width: 150, height: 166 },
    className = '',
    onFile,
    onClose,
    onlyPreview = false,
    thumbnail,
    mediaRerender,
    showFileName = false,
    showFileSize = false,
    template = 1,
    list,
    onViewEdit,
    enableEvent = false,
    videoProps
  } = props;

  const file = propFile?.file || propFile;
  const uploadState = useUploadProgress(file);
  const { downloading, handleDownload } = useDownload();

  // Any field the server pushes for this file (status, deleteAt, name, size,
  // url, ...) accumulates here. Reset whenever the caller swaps in a
  // different file (id/counter/name changes), so stale updates from a
  // previous file never leak onto the new one.
  const [eventUpdates, setEventUpdates] = useState({});
  useEffect(() => {
    setEventUpdates({});
  }, [file?.counter ?? file?.id ?? file?.name]);

  // When enabled, listens for server-pushed updates on this file's own
  // processing channel and applies them locally in real time — instead of
  // relying on the caller to mutate `file` and bump `mediaRerender` itself.
  // Payload shape: { status?, deleteAt?, ...any other file field }.
  useEffect(() => {
    if (!enableEvent || !propFile?.fileProcessId) return;
    const eventName = 'ws:file_process:' + propFile.fileProcessId;
    const handleFileProcessEvent = (payload) => {
      if (!payload) return;
      setEventUpdates((prev) => ({ ...prev, ...payload }));
    };
    events.on(eventName, handleFileProcessEvent);
    return () => events.cancel(eventName, handleFileProcessEvent);
  }, [enableEvent, propFile?.fileProcessId]);

  // While uploading, `file` is a real File/Blob instance (propFile = { file: File, ... }).
  // Its name/size/type are prototype getters, not own properties, so spreading it
  // (`{ ...file }`) would silently drop them and turn it into a plain object —
  // breaking anything that needs the real Blob (e.g. URL.createObjectURL in
  // handleDownload). So for that case we mutate the File instance in place instead
  // (same pattern the component already uses for `onUploadProgress`), keeping it a
  // real File. Once upload is done, `file` is a plain data object ({ uri, status, ... })
  // and a normal immutable spread-merge is safe.
  const isRawFile = typeof Blob !== 'undefined' && file instanceof Blob;
  const mergedFile = useMemo(() => {
    if (!file) return file || {};
    if (isRawFile) {
      // Blob/File's own props (size, type, name, lastModified) are
      // getter-only — assigning to them throws. Those are already correct
      // on the real File anyway, so set what we can and skip the rest.
      Object.keys(eventUpdates).forEach((key) => {
        try {
          file[key] = eventUpdates[key];
        } catch (e) {
          console.debug(`FilePreviewBox: cannot assign to file.${key} (read-only)`, e);
          // read-only native Blob/File property — nothing to update
        }
      });
      return file || {};
    }
    return { ...file, ...eventUpdates };
  }, [file, eventUpdates, isRawFile]);

  // VideoPreview takes the wrapper (propFile), not the plain file, so give it
  // the same event-updated values whenever propFile wraps a `file` field.
  const mergedPropFile = useMemo(() => {
    if (!propFile) return propFile;
    return propFile.file ? { ...propFile, file: mergedFile } : mergedFile;
  }, [propFile, mergedFile]);

  const rawType = useFileType(mergedFile);

  const { remainingMs, expired: timeExpired } = useCountdown(mergedFile.deleteAt);

  const expired = mergedFile.status === 'expired' || timeExpired;
  const type = expired ? 'other' : rawType;

  // Guards the first-view API so it's only ever called once per file, even
  // if the user clicks download/view several times before it resolves.
  const firstViewCalledRef = useRef(false);
  useEffect(() => {
    firstViewCalledRef.current = false;
  }, [file?.counter ?? file?.id ?? file?.name]);

  if (!file) return null;
  if (uploadState?.status === 'aborted') return null;

  // When enabled, pings the caller-provided first-view API — once — on a
  // download/view action, but only while the file has no deleteAt yet and
  // isn't already expired (once either is true there's nothing left to
  // activate). The API is expected to tell the backend "this file was
  // viewed" and, on an actual first view, to start the expiry countdown
  // server-side; whatever deleteAt it returns is applied locally so the
  // countdown badge starts ticking right away.
  const maybeCallFirstView = () => {
    if (!enableEvent || mergedFile?.selfDestruct?.type !== 'first_view') return;
    if (mergedFile.deleteAt || expired) return; // deleteAt already set, or file already expired
    if (firstViewCalledRef.current) return; // already called once for this file
    firstViewCalledRef.current = true;

    Promise.resolve(onFirstView(mergedFile))
      .then((result) => {
        const newDeleteAt = result?.deleteAt ?? (typeof result === 'number' ? result : null);
        if (newDeleteAt) setEventUpdates((prev) => ({ ...prev, deleteAt: newDeleteAt }));
      })
      .catch((error) => {
        console.error('first-view API error:', error);
        firstViewCalledRef.current = false; // allow a retry on the next click since this one failed
      });
  };

  const openDicomViewer = (e) => {
    if (e) e.stopPropagation();
    maybeCallFirstView();
    const url = `/assets/ohif/viewer/orthanc?StudyInstanceUIDs=${mergedFile.allStudyUIDs?.join(',') || ''}`;
    window.open(url, '_blank');
  };

  // Clicking an image/video opens it directly via onFile (e.g. an edit modal)
  // when the caller provided one; otherwise it opens the fullscreen slide
  // viewer over every image/video in `list`.
  const handleMediaClick = () => {
    maybeCallFirstView();
    if (onFile) return onFile(mergedFile);
    openSlideMedia({
      file: mergedFile,
      list: list.map((item) => (item === propFile ? mergedFile : item)),
      onViewEdit,
      enableEvent
    });
  };

  // Progress bar shown while the file is uploading.
  // scss template !== 2 (detail-row layout) hides the upload engine label and
  // always shows the byte counter unless the box is small.
  const isSmall = styles.width < 140;
  const progressProps = {
    ...uploadState,
    file: mergedFile,
    small: isSmall,
    ...(template !== 2 ? { engine: false, showBytes: !isSmall } : {})
  };

  // Wraps handleDownload with the first-view ping — used everywhere a click
  // triggers an actual file download (icon click, detail bar, DICOM Save).
  const handleDownloadWithFirstView = (e, targetFile) => {
    maybeCallFirstView();
    return handleDownload(e, targetFile);
  };

  // What clicking the name bar does depends on the file type — mirrors the
  // original per-type onClick wiring (renderDetail's own click handler
  // already no-ops for DICOM regardless of what's passed here).
  const handleDetailClick = () => {
    if (expired) return; // nothing to open/download for an expired file
    if (type === 'image' || type === 'video') return handleMediaClick();
    if (type === 'dcm') return; // dcm uses its own Save/View buttons; audio has no click action
    return handleDownloadWithFirstView(undefined, mergedFile); // other/generic files: same as clicking the icon
  };

  const previewNode = renderPreviewByType({
    type,
    file: mergedFile,
    propFile: mergedPropFile,
    width: styles.width ? styles.width : expired && styles.maxWidth,
    height: styles.height,
    mediaRerender,
    onAudioClick: maybeCallFirstView,
    onImgClick: handleMediaClick,
    onDcmClick: openDicomViewer,
    onDownloadClick: expired ? undefined : (e) => handleDownloadWithFirstView(e, mergedFile),
    thumbnail,
    downloading,
    fileId,
    onlyPreview,
    styles,
    expired,
    videoProps
  });

  // template = 2 with no detail
  const isFullPreviewBox = !showFileName && (rawType === 'image' || rawType === 'video');

  const specialAudioCase = template === 2 && !onlyPreview && rawType === 'audio';

  return (
    <FilePreviewBoxContainer
      template={template}
      width={styles.width}
      height={styles.height}
      className={className}
    >
      {template === 2 && !specialAudioCase ? (
        // Template 2: fixed-size thumbnail on the left, no close-button overlay
        // on it (scss doesn't style one for this template) — the close button
        // instead sits at the end of the row, like a list-item remove action.
        <View
          style={{
            maxWidth: styles.maxWidth,
            maxHeight: styles.maxHeight,
            width: !styles.width && styles.maxWidth ? '100%' : styles.width,
            height: !styles.height && styles.maxHeight ? '100%' : styles.height,
            flexShrink: 0
          }}
        >
          {previewNode}
        </View>
      ) : (
        <>
          {previewNode}
          <CloseButton onClose={onClose} file={mergedFile} template={template} />
        </>
      )}
      {expired && isFullPreviewBox && styles?.width > 150 && (
        <MCText
          textLight
          weight={400}
          size={18}
          numberOfLines={1}
          style={{
            textAlign: 'center',
            position: 'absolute',
            width: '100%',
            bottom: remCss(10)
          }}
        >
          {lang.t(LANG_CONST.text.file_expired)}
        </MCText>
      )}
      <FileDetail
        file={mergedFile}
        showFileName={showFileName || (specialAudioCase && expired)}
        showFileSize={showFileSize}
        isDcm={type === 'dcm'}
        expired={expired}
        template={template}
        onClick={handleDetailClick}
        onSave={(e) => handleDownloadWithFirstView(e, mergedFile)}
        onView={(e) => openDicomViewer(e)}
        remainingMs={remainingMs}
      />

      <CountDownBadge
        isOverplay={isFullPreviewBox}
        remainingMs={!expired ? remainingMs : null}
        showIcon={mergedFile?.selfDestruct?.type === 'first_view' && !expired}
      />
      {template === 2 && <CloseButton onClose={onClose} file={mergedFile} template={template} />}
      {uploadState.status && <UploadProgress {...progressProps} />}
    </FilePreviewBoxContainer>
  );
}

export default FilePreviewBox;
